import { BACKEND_CONFIG } from '../config/backendConfig';
import type { NoteCategory } from '../utils/noteCategories';
const BASE_URL = `${BACKEND_CONFIG.baseURL}`;

function getToken(): string | null {
  // Use sessionStorage token (same key as AuthContext.tsx sets)
  return sessionStorage.getItem('sessionToken') || localStorage.getItem('sessionToken') || localStorage.getItem('token');
}

// ===== INTERFACES =====

export interface StaffPlayer {
  athlete_profile_id: string;
  name: string;
  email: string;
  city: string | null;
  state: string | null;
  grad_year: number | null;
  profile_picture_url: string | null;
  positions: string[] | null;
  sport: string | null;
  current_gpa: number | null;
  team_id: string;
  team_name: string | null;
  last_comm: string | null;
  last_note: string | null;
  school_count?: number;
  total_activity?: number;
  profile_views?: number;
  emails_sent?: number;
  tweets_sent?: number;
}

export interface StaffPlayerDetail extends StaffPlayer {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  date_of_birth: string | null;
  bats: string | null;
  throws: string | null;
  height: string | null;
  weight: number | null;
  awards: string[] | null;
  current_gpa: number | null;
  sat_total: number | null;
  act_total: number | null;
  gpa_scale: string | null;
  school_lists: SchoolListItem[];
  teams: TeamMembership[];
  notes_count: number;
  communications_count: number;
}

export interface StaffCommunication {
  id: string;
  channel: 'email' | 'tweet' | 'dm';
  subject: string | null;
  message_preview: string | null;
  full_message: string | null;
  recipient_info: Record<string, any> | null;
  staff_name: string;
  created_at: string;
}



export interface PlayerNote {
  id: string;
  author_id: string;
  author_name: string;
  athlete_profile_id: string;
  team_id: string;
  note_text: string;
  category: NoteCategory;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface StaffAnalyticsData {
  communications_by_channel: Array<{ channel: string; count: string }>;
  notes_by_category: Array<{ category: string; count: string }>;
  teams: Array<{ id: string; team_name: string; athlete_count: string }>;
  communication_trends: Array<{ date: string; channel: string; count: string }>;
  period_days: number;
}

export interface School {
  school_id: number;
  school_name: string;
  division: string | null;
  conference: string | null;
  athlete_count: string;
  dream: string;
  target: string;
  safety: string;
}

interface SchoolListItem {
  school_id: string;
  category: string;
  school_name: string;
  division: string | null;
  conference?: string | null;
  logo_url?: string | null;
  added_date: string;
}

interface TeamMembership {
  team_id: string;
  team_name: string;
  joined_at: string;
}

async function staffFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  let data: any;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }
    throw new Error('Invalid response format');
  }
  if (!response.ok) {
    const errorMsg = typeof data.error === 'string' ? data.error : data.error?.message || `Request failed: ${response.status}`;
    throw new Error(errorMsg);
  }
  return data;
}

// ===== PLAYER ROSTER =====

export interface PlayerFilters {
  team_id?: string;
  search?: string;
  position?: string;
  grad_year?: string;
  page?: number;
  limit?: number;
  // Super-admin only: include athletes with no active team membership.
  // Used by the admin "Manage Athletes" page so it can assign teams.
  include_unassigned?: boolean;
}

export async function getPlayers(filters: PlayerFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) {
      params.append(key, String(value));
    }
  });
  return staffFetch(`/staff/players?${params}`);
}

// Stable full team list (admin) — used by Manage Athletes so the Team filter isn't
// limited to whatever teams happen to be on the current page of results.
export async function getAllTeams(): Promise<{ teams: Array<{ id: string; team_name: string; org_name?: string }> }> {
  return staffFetch(`/admin-assign/teams`);
}

export async function getPlayerDetail(athleteId: string) {
  return staffFetch(`/staff/players/${athleteId}`);
}

export async function getPlayerCommunications(athleteId: string) {
  return staffFetch(`/staff/players/${athleteId}/communications`);
}

export async function getPlayerSchoolLists(athleteId: string, includeCoaches: boolean = false) {
  const params = includeCoaches ? '?include_coaches=true' : '';
  return staffFetch(`/staff/players/${athleteId}/school-lists${params}`);
}

// ===== NOTES =====

export async function getPlayerNotes(athleteId: string, params: { category?: string; page?: number; limit?: number } = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) searchParams.append(key, String(value));
  });
  return staffFetch(`/staff/players/${athleteId}/notes?${searchParams}`);
}

export async function createNote(athleteId: string, note_text: string, category: string = 'general') {
  return staffFetch(`/staff/players/${athleteId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note_text, category }),
  });
}

export async function updateNote(noteId: string, data: { note_text?: string; category?: string }) {
  return staffFetch(`/staff/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNote(noteId: string) {
  return staffFetch(`/staff/notes/${noteId}`, { method: 'DELETE' });
}

// ===== ATHLETE-FACING NOTES =====

export async function getMyNotes(params: { page?: number; limit?: number } = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) searchParams.append(key, String(value));
  });
  return staffFetch(`/player/notes?${searchParams}`);
}

export async function markNoteRead(noteId: string) {
  return staffFetch(`/player/notes/${noteId}/read`, { method: 'PUT' });
}

export async function markAllNotesRead() {
  return staffFetch('/player/notes/read-all', { method: 'PUT' });
}

// ===== NOTE REPLIES (APS-253) =====
// The notes lists carry `replies_enabled: true` and a `replies` array per note
// when the server supports replies. Show reply controls only then.

export interface NoteReply {
  id: string;
  note_id: string;
  author_name: string;
  author_role: 'staff' | 'athlete' | 'guardian';
  body: string;
  created_at: string;
  is_mine: boolean;
}

/** Staff side: reply under a note on a player's profile. */
export async function createStaffNoteReply(noteId: string, body: string): Promise<{ success: boolean; reply: NoteReply }> {
  return staffFetch(`/staff/notes/${noteId}/replies`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export async function deleteStaffNoteReply(noteId: string, replyId: string) {
  return staffFetch(`/staff/notes/${noteId}/replies/${replyId}`, { method: 'DELETE' });
}

/** Family side: the player or a guardian replies under a coach's note. */
export async function createMyNoteReply(noteId: string, body: string): Promise<{ success: boolean; reply: NoteReply }> {
  return staffFetch(`/player/notes/${noteId}/replies`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export async function deleteMyNoteReply(noteId: string, replyId: string) {
  return staffFetch(`/player/notes/${noteId}/replies/${replyId}`, { method: 'DELETE' });
}

// ===== COMMUNICATION LOGGING =====

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  scope: 'personal' | 'team' | 'org' | 'system';
  variables: string[];
}

export async function getEmailTemplates(scope?: string) {
  const params = scope ? `?scope=${scope}` : '';
  return staffFetch(`/email-templates${params}`);
}

export interface LogCommunicationData {
  channel: 'email' | 'tweet' | 'dm';
  recipient_info?: Record<string, any>;
  subject?: string;
  message_preview?: string;
  external_id?: string;
}

export async function logCommunication(athleteId: string, data: LogCommunicationData) {
  return staffFetch(`/staff/players/${athleteId}/log-communication`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function logSchoolCommunication(data: LogCommunicationData) {
  return staffFetch('/staff/log-school-communication', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ===== PROFILE =====

export async function updateStaffProfile(data: { name?: string; phone?: string }) {
  return staffFetch('/staff/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Upload staff profile picture (POST /staff/profile-picture with S3 + thumbnails)
export async function uploadStaffProfilePicture(file: File) {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('profilePicture', file);

  const response = await fetch(`${BASE_URL}/staff/profile-picture`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Server returned an invalid response');
  }
  if (!response.ok) {
    throw new Error(data.error || 'Failed to upload profile picture');
  }
  return data;
}

// ===== ANALYTICS =====

export async function getAnalyticsOverview(period: number = 30) {
  return staffFetch(`/staff/analytics/overview?period=${period}`);
}

export async function getAnalyticsCommunications(params: { channel?: string; page?: number; limit?: number } = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) searchParams.append(key, String(value));
  });
  return staffFetch(`/staff/analytics/communications?${searchParams}`);
}

export async function getPlayerAnalytics(athleteId: string) {
  return staffFetch(`/staff/analytics/player/${athleteId}`);
}

export async function exportAnalytics(period: number = 30): Promise<Blob> {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${BASE_URL}/staff/analytics/export?period=${period}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to export analytics');
  return response.blob();
}

export async function getSchoolAggregation(params: {
  sort_by?: string;
  sort_dir?: string;
  min_athletes?: number;
} = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) searchParams.append(key, String(value));
  });
  return staffFetch(`/staff/analytics/school-aggregation?${searchParams}`);
}

export async function getSchoolAthletes(schoolId: number) {
  return staffFetch(`/staff/analytics/school/${schoolId}/athletes`);
}

export async function getSchoolCoaches(schoolIds: number[]): Promise<{ success: boolean; coaches: Record<string, Array<{ id: string; name: string; email: string; title: string; phone?: string; twitter?: string }>> }> {
  return staffFetch(`/staff/analytics/school-aggregation/coaches?school_ids=${schoolIds.join(',')}`);
}

export async function getPlayerShareUrl(athleteId: string): Promise<{ success: boolean; shareUrl: string | null }> {
  return staffFetch(`/staff/players/${athleteId}/share-url`);
}

export async function removePlayerFromTeam(athleteId: string): Promise<{ success: boolean; message: string; promoRevoked: boolean }> {
  return staffFetch(`/staff/players/${athleteId}/remove`, { method: 'POST' });
}

export async function exportSchoolAggregation(): Promise<Blob> {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${BASE_URL}/staff/analytics/school-aggregation/export`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to export school data');
  return response.blob();
}

// ===== ENGAGEMENT ANALYTICS (aggregated across athletes) =====

export interface EngagementFilters {
  teamId?: string;
  athleteId?: string;
}

function buildEngagementParams(filters: EngagementFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.teamId) params.append('teamId', filters.teamId);
  if (filters.athleteId) params.append('athleteId', filters.athleteId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getEngagementOverview(filters: EngagementFilters = {}) {
  return staffFetch(`/staff/analytics/engagement/overview${buildEngagementParams(filters)}`);
}

export async function getEngagementCollegeBreakdown(metric: string = 'emails_sent', filters: EngagementFilters = {}) {
  const params = new URLSearchParams();
  params.append('metric', metric);
  if (filters.teamId) params.append('teamId', filters.teamId);
  if (filters.athleteId) params.append('athleteId', filters.athleteId);
  return staffFetch(`/staff/analytics/engagement/college-breakdown?${params}`);
}

export async function getEngagementEmailFunnel(filters: EngagementFilters = {}) {
  return staffFetch(`/staff/analytics/engagement/email-funnel${buildEngagementParams(filters)}`);
}

export async function getEngagementSocialTrends(filters: EngagementFilters = {}) {
  return staffFetch(`/staff/analytics/engagement/social-trends${buildEngagementParams(filters)}`);
}

export async function getEngagementEmailTrends(filters: EngagementFilters = {}) {
  return staffFetch(`/staff/analytics/engagement/email-trends${buildEngagementParams(filters)}`);
}

export async function getEngagementCollegeEngagement(filters: EngagementFilters = {}) {
  return staffFetch(`/staff/analytics/engagement/college-engagement${buildEngagementParams(filters)}`);
}

export async function getEngagementTeams() {
  return staffFetch<{ teams: Array<{ id: string; team_name: string; player_count: number }> }>('/staff/analytics/engagement/teams');
}

export async function getEngagementPlayers(teamId?: string) {
  const params = teamId ? `?teamId=${teamId}` : '';
  return staffFetch<{ players: Array<{ id: string; name: string; grad_year?: number }> }>(`/staff/analytics/engagement/players${params}`);
}

// ===== TEAM CODE =====

export async function validateTeamCode(code: string) {
  return staffFetch('/team-code/validate', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function redeemTeamCode(code: string, athleteProfileId?: string) {
  return staffFetch('/team-code/redeem', {
    method: 'POST',
    body: JSON.stringify({ code, ...(athleteProfileId && { athlete_profile_id: athleteProfileId }) }),
  });
}

export interface AthleteSearchResult {
  athlete_profile_id: string;
  name: string;
  email: string | null;
  guardian_email: string | null;
  city: string | null;
  state: string | null;
  grad_year: number | null;
  profile_picture_url: string | null;
  positions: string[] | null;
  sport: string | null;
  bats: string | null;
  throws: string | null;
  height: string | null;
  weight: number | null;
  current_gpa: number | null;
}

export async function searchAthletes(q: string, excludeTeamId?: string): Promise<{ success: boolean; athletes: AthleteSearchResult[] }> {
  const params = new URLSearchParams({ q });
  if (excludeTeamId) params.append('exclude_team_id', excludeTeamId);
  return staffFetch(`/staff/athlete-search?${params}`);
}

export async function assignAthleteToTeam(teamId: string, athleteProfileId: string) {
  return staffFetch('/staff/team-assign', {
    method: 'POST',
    body: JSON.stringify({ team_id: teamId, athlete_profile_id: athleteProfileId }),
  });
}
